import { useState, useEffect } from 'react';
import { MessageSquare, Loader2, Pencil, Save, X, Check, AlertCircle } from 'lucide-react';

interface AutoReply {
  id: number;
  key: string;
  name: string;
  message: string;
  isActive: number;
  description?: string;
  updatedAt?: number;
}

interface WhatsAppAutoRepliesSectionProps {
  apiFetch: (path: string, init?: RequestInit) => Promise<Response>;
}

export default function WhatsAppAutoRepliesSection({ apiFetch }: WhatsAppAutoRepliesSectionProps) {
  const [items, setItems] = useState<AutoReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/auto-replies');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '載入失敗');
      setItems(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入自動回覆失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const startEdit = (item: AutoReply) => {
    setEditingKey(item.key);
    setEditMessage(item.message);
    setEditActive(item.isActive !== 0);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditMessage('');
    setEditActive(true);
  };

  const saveEdit = async (key: string) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiFetch(`/auto-replies/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: editMessage, isActive: editActive }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setSuccess('自動回覆已更新');
      setEditingKey(null);
      await fetchItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (ts?: number) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleString('zh-HK');
  };

  const variableHint = (key: string) => {
    if (key === 'order_confirmation_fallback') {
      return '可用變數：{{orderNum}}、{{payable}}、{{discountNote}}、{{bankAccount}}、{{bankFps}}、{{bankCompanyName}}、{{proofUrl}}';
    }
    return '純文字訊息，無變數';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3 mb-4">
          <AlertCircle className="text-orange-600 mt-0.5" size={20} />
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">24 小時自動回覆</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              以下訊息只在客戶主動發訊後的 24 小時「對話窗口」內，以自由文字發送。客戶不會經 CloudWAPI 收到這些訊息。
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm flex items-center gap-2">
            <Check size={16} /> {success}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            <Loader2 size={18} className="animate-spin" /> 載入中...
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => {
              const isEditing = editingKey === item.key;
              return (
                <div
                  key={item.key}
                  className={`border rounded-xl p-4 transition ${
                    item.isActive
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/50 opacity-70'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <MessageSquare size={16} className="text-orange-600" />
                        <h4 className="font-bold text-gray-900 dark:text-white">{item.name}</h4>
                        {item.isActive ? (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 rounded-full">
                            啟用
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-full">
                            停用
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.description}</p>
                      )}
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => startEdit(item)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition"
                      >
                        <Pencil size={14} /> 編輯
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-3">
                      <textarea
                        value={editMessage}
                        onChange={(e) => setEditMessage(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">{variableHint(item.key)}</p>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={editActive}
                            onChange={(e) => setEditActive(e.target.checked)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                          啟用
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => saveEdit(item.key)}
                          disabled={saving}
                          className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition"
                        >
                          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                          儲存
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={saving}
                          className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                        >
                          <X size={14} /> 取消
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg p-3">
                      {item.message}
                    </div>
                  )}

                  <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                    最後更新：{formatDate(item.updatedAt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
